import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CourseResourceApi } from '../services/api/CourseResourceApi';
import './CourseResourcesPage.css';

const KIND_LABELS = {
  quiz: 'Quiz',
  flashcard_deck: 'Flashcards',
  audio: 'Audio',
};

/**
 * Student-facing list of course-owned shareable resources.
 * Route: /course/:courseId/resources
 *
 * Backend gates listing through SecurityService.canViewEndeavor; users without
 * access see a 403 surface. Submitting an attempt always records the calling
 * user (server-side override).
 */
function CourseResourcesPage() {
  const { courseId } = useParams();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeKind, setActiveKind] = useState('all');
  const [openResource, setOpenResource] = useState(null);
  const [myAttempts, setMyAttempts] = useState({}); // resourceId -> attempts[]

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await CourseResourceApi.list(courseId);
      if (resp.status === 401 || resp.status === 403) {
        setError('You do not have access to this course.');
        return;
      }
      if (!resp.ok) {
        setError(`Failed to load resources (${resp.status})`);
        return;
      }
      const data = await resp.json();
      setResources(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || 'Network error loading resources');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { reload(); }, [reload]);

  const loadAttempts = useCallback(async (resourceId) => {
    try {
      const resp = await CourseResourceApi.listAttempts(courseId, resourceId);
      if (!resp.ok) return;
      const data = await resp.json();
      setMyAttempts(prev => ({ ...prev, [resourceId]: Array.isArray(data) ? data : [] }));
    } catch (_) { /* swallow */ }
  }, [courseId]);

  const onOpen = (resource) => {
    setOpenResource(resource);
    if (resource && !myAttempts[resource.resourceId]) {
      loadAttempts(resource.resourceId);
    }
  };

  const onMarkComplete = async (resource) => {
    setError(null);
    const resp = await CourseResourceApi.submitAttempt(courseId, resource.resourceId, {
      completedAt: new Date().toISOString(),
    });
    if (!resp.ok) {
      setError(`Failed to record attempt (${resp.status})`);
      return;
    }
    loadAttempts(resource.resourceId);
  };

  const visible = activeKind === 'all'
    ? resources
    : resources.filter(r => r.kind === activeKind);

  return (
    <div className="course-resources-page">
      <header className="course-resources-header">
        <div>
          <h1>Course Resources</h1>
          <Link to={`/course/${courseId}`} className="course-resources-back-link">← Back to course</Link>
        </div>
      </header>

      <nav className="course-resources-filter" role="tablist">
        {[['all', 'All'], ['quiz', 'Quizzes'], ['flashcard_deck', 'Flashcards'], ['audio', 'Audio']].map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={activeKind === k}
            className={`course-resources-filter-btn ${activeKind === k ? 'active' : ''}`}
            onClick={() => setActiveKind(k)}
          >{label}</button>
        ))}
      </nav>

      {loading && <div className="course-resources-loading">Loading…</div>}
      {error && <div className="course-resources-error" role="alert">{error}</div>}

      {!loading && visible.length === 0 && (
        <div className="course-resources-empty">No resources available yet.</div>
      )}

      <ul className="course-resources-list">
        {visible.map(r => (
          <li key={r.resourceId} className="course-resources-card">
            <div className="course-resources-card-main">
              <div className="course-resources-kind-pill">{KIND_LABELS[r.kind] || r.kind}</div>
              <h3>{r.title}</h3>
              {r.description && <p className="course-resources-desc">{r.description}</p>}
            </div>
            <div className="course-resources-card-actions">
              <button onClick={() => onOpen(r)}>Open</button>
              <button onClick={() => onMarkComplete(r)}>Mark Complete</button>
            </div>
          </li>
        ))}
      </ul>

      {openResource && (
        <div className="course-resources-modal-backdrop" onClick={() => setOpenResource(null)}>
          <div className="course-resources-modal" onClick={e => e.stopPropagation()}>
            <header>
              <h2>{openResource.title}</h2>
              <button className="course-resources-modal-close" onClick={() => setOpenResource(null)}>×</button>
            </header>
            <div className="course-resources-modal-body">
              {openResource.description && <p>{openResource.description}</p>}
              {openResource.payloadRef && (() => {
                // payloadRef is a free-form pointer. AI-generated artifacts use
                // an opaque "kind:id" form (e.g. "quiz_set:42",
                // "flashcard_deck:7", "audio_content:11", "document:9"). Only
                // render as a clickable link when it actually parses as an
                // http(s) URL — otherwise show as plain text so we don't emit
                // bogus <a href="quiz_set:42"> links.
                const ref = openResource.payloadRef;
                const isUrl = /^https?:\/\//i.test(ref);
                return (
                  <p>
                    <strong>Resource:</strong>{' '}
                    {isUrl ? (
                      <a href={ref} target="_blank" rel="noopener noreferrer">{ref}</a>
                    ) : (
                      <span className="course-resources-payload-ref">{ref}</span>
                    )}
                  </p>
                );
              })()}
              <h4>Your attempts</h4>
              {(myAttempts[openResource.resourceId] || []).length === 0 ? (
                <div className="course-resources-empty">No attempts yet.</div>
              ) : (
                <ul className="course-resources-attempts">
                  {(myAttempts[openResource.resourceId] || []).map(a => (
                    <li key={a.attemptId}>
                      Started {new Date(a.startedAt).toLocaleString()}
                      {a.completedAt && ` · completed ${new Date(a.completedAt).toLocaleString()}`}
                      {a.scorePct != null && ` · ${a.scorePct}%`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseResourcesPage;
